import {
  AssuredWorkloadOutlined,
  Policy,
  SafetyCheck,
  ScreenShareOutlined,
  StopScreenShareOutlined,
  SwitchLeft,
} from "@mui/icons-material";
import { Checkbox } from "@mui/material";
import { UserLocale } from "@prisma/client";
import { ChevronsUpDown, Container, History, Lock, LogOut } from "lucide-react";
import type React from "react";
import { useEffect } from "react";
import { useToggle } from "react-use";
import packageJson from "~/../package.json";
import { DevUserRoleSelector } from "~/components/layout/DevUserRoleSelector";
import { ToggleSuperAdmin } from "~/components/layout/ToggleSuperAdmin";
import { ImpersonatePanel } from "~/components/modals/ImpersonatePanel";
import { RequestExceptionalImpersonationPanel } from "~/components/modals/RequestExceptionalImpersonationPanel";
import { RequestTemporarySuperAdminRightsPanel } from "~/components/modals/RequestTemporarySuperAdminRightsPanel";
import { Link } from "~/components/ui/core/Link";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/shadcn/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/shadcn/ui/dropdown-menu";
import {
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "~/components/ui/shadcn/ui/sidebar";
import { config } from "~/config";
import { usePermissions } from "~/hooks/usePermissions";
import { useSession } from "~/hooks/useSession";
import { useI18n } from "~/lib/i18n/useI18n";
import { getFiguresCompanyId } from "~/lib/utils";
import { useCanSwitchCompaniesQuery } from "~/pages/api/auth/can-switch-companies";
import { useStopCompanyImpersonationMutation } from "~/pages/api/auth/company-impersonation/stop-company-impersonation";
import { useUpdateUserLocaleQuery } from "~/pages/api/user/update-user-locale";
import { formatUserFullName } from "~/services/user/utils";

export const SidebarAccount: React.FC = () => {
  const { t } = useI18n();
  const { user, impersonatingCompany, impersonatingUser, signOut } = useSession();
  const { permissions } = usePermissions();
  const { isMobile } = useSidebar();

  const canSwitchCompaniesQuery = useCanSwitchCompaniesQuery({
    options: {
      placeholderData: { canSwitchCompanies: false },
      enabled: !!user,
    },
  });

  const updateUserLocale = useUpdateUserLocaleQuery({
    onSuccess: () => {
      window.location.reload();
    },
  });

  const stopCompanyImpersonationMutation = useStopCompanyImpersonationMutation();

  const { showImpersonatePanel, toggleImpersonatePanel } = useImpersonateShortcut();
  const [showRequestExceptionalImpersonationPanel, toggleRequestExceptionalImpersonationPanel] = useToggle(false);
  const [showRequestTemporarySuperAdminRightsPanel, toggleRequestTemporarySuperAdminRightsPanel] = useToggle(false);
  const [isFrench, toggleIsFrench] = useToggle(user?.locale === UserLocale.FR);

  if (!user) return null;

  const signOutAndRedirect = async () => {
    await signOut();
    window.location.replace(config.app.isProduction ? config.app.websiteUrl : config.app.url);
  };

  const showSwitchCompany = canSwitchCompaniesQuery.data.canSwitchCompanies;
  const showBackoffice = user.isSuperAdmin;
  const showSuperAdminRequest =
    !user.isSuperAdmin && permissions.canRequestSuperAdminRights && user.companyId === getFiguresCompanyId();
  const showImpersonate = user.isSuperAdmin;
  const showStopImpersonate = user.isSuperAdmin && impersonatingCompany;
  const showLocaleSwitch = true;
  const showPrivacyPolicy = true;
  const showEnvironment = !config.app.isProduction;
  const showVersion = !config.app.isProduction || user.isSuperAdmin;
  const showDevUserRoleSelector = config.app.isLocal && !impersonatingCompany && !impersonatingUser;

  return (
    <SidebarFooter className="pt-0">
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.profilePictureUrl} alt={formatUserFullName(user)} />
                  <AvatarFallback className="rounded-lg">
                    {user.firstName.charAt(0)}
                    {user.lastName.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{formatUserFullName(user)}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage src={user.profilePictureUrl} alt={formatUserFullName(user)} />
                    <AvatarFallback className="rounded-lg">
                      {user.firstName.charAt(0)}
                      {user.lastName.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{formatUserFullName(user)}</span>
                    <span className="truncate text-xs">{user.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              {showSwitchCompany && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={{ pathname: "/auth/company-picker" }} noLinkStyle>
                    <SwitchLeft />
                    {t("components.layout.sidebar.switch-company")}
                  </Link>
                </DropdownMenuItem>
              )}

              {showBackoffice && (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={{ pathname: "/admin" }} noLinkStyle>
                    <AssuredWorkloadOutlined />
                    {t("components.layout.sidebar.backoffice")}
                  </Link>
                </DropdownMenuItem>
              )}

              {showSuperAdminRequest && (
                <DropdownMenuItem onClick={() => toggleRequestTemporarySuperAdminRightsPanel()}>
                  <SafetyCheck />
                  {t("components.layout.sidebar.request-super-admin-rights")}
                </DropdownMenuItem>
              )}

              {showImpersonate && (
                <DropdownMenuItem className="cursor-pointer" onClick={toggleImpersonatePanel}>
                  <ScreenShareOutlined />
                  {t("components.layout.sidebar.impersonate")}
                </DropdownMenuItem>
              )}

              {showImpersonate && (
                <DropdownMenuItem className="cursor-pointer" onClick={toggleRequestExceptionalImpersonationPanel}>
                  <Policy />
                  {t("components.layout.sidebar.request-impersonation")}
                </DropdownMenuItem>
              )}

              {showStopImpersonate && (
                <DropdownMenuItem className="cursor-pointer" onClick={stopCompanyImpersonationMutation.mutate}>
                  <StopScreenShareOutlined />
                  {t("components.layout.sidebar.stop-impersonate")}
                </DropdownMenuItem>
              )}

              {showLocaleSwitch && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={async () => {
                    toggleIsFrench();
                    await updateUserLocale.mutateAsync({});
                  }}
                >
                  <Checkbox size="small" className="size-4 p-0" checked={isFrench} />
                  {t("components.layout.sidebar.french")}
                </DropdownMenuItem>
              )}

              {showPrivacyPolicy && (
                <DropdownMenuItem className="cursor-pointer">
                  <Lock />
                  <a
                    href={
                      isFrench
                        ? "https://figures.hr/other/politique-de-confidentialite-de-lapplication"
                        : "https://figures.hr/other/application-privacy-policy"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("components.layout.sidebar.privacy-policy")}
                  </a>
                </DropdownMenuItem>
              )}

              {showEnvironment && (
                <DropdownMenuItem className="cursor-pointer">
                  <Container />
                  {t("components.layout.sidebar.environment", { environment: config.app.environmentName })}
                </DropdownMenuItem>
              )}

              {showVersion && (
                <DropdownMenuItem className="cursor-pointer">
                  <History />
                  {t("components.layout.sidebar.version", { version: packageJson.version })}
                </DropdownMenuItem>
              )}

              {showDevUserRoleSelector && (
                <>
                  <DropdownMenuSeparator />
                  <DevUserRoleSelector />
                  <ToggleSuperAdmin />
                </>
              )}

              <DropdownMenuSeparator />

              <DropdownMenuItem className="cursor-pointer" onClick={signOutAndRedirect}>
                <LogOut />
                {t("components.layout.sidebar.log-out")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      {showImpersonate && <ImpersonatePanel isOpen={showImpersonatePanel} onClose={() => toggleImpersonatePanel()} />}

      {showImpersonate && (
        <RequestExceptionalImpersonationPanel
          isOpen={showRequestExceptionalImpersonationPanel}
          onClose={() => toggleRequestExceptionalImpersonationPanel()}
        />
      )}

      {showSuperAdminRequest && (
        <RequestTemporarySuperAdminRightsPanel
          isOpen={showRequestTemporarySuperAdminRightsPanel}
          onClose={() => toggleRequestTemporarySuperAdminRightsPanel()}
        />
      )}
    </SidebarFooter>
  );
};

const useImpersonateShortcut = () => {
  const { user } = useSession();
  const [showImpersonatePanel, toggleImpersonatePanel] = useToggle(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey && event.key === "i" && user?.isSuperAdmin) {
        toggleImpersonatePanel();
        event.preventDefault();
        return;
      }
    };

    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [user?.isSuperAdmin, showImpersonatePanel]);

  return { showImpersonatePanel, toggleImpersonatePanel };
};
